import type { Filter, FrameCategory, FrameOption, Sticker } from '@renderer/types/configState'

/** The three catalogue responses exactly as the API returns them (no store writes). */
export interface RawProjectCatalog {
  filters: Filter[]
  frameData: {
    categoryModeEnabled?: boolean
    frameCategories?: Array<FrameCategory & { frames: FrameOption[] }>
    frames?: FrameOption[]
  }
  stickers: Sticker[]
}

const apiUrl = (): string => process.env.VITE_API_URL || ''

/**
 * Fetch a project's filters, frames and stickers. Pure network — the caller
 * decides whether to apply the result to the stores (`applyProjectCatalog` in
 * sessionState) or only to warm the offline cache (`prefetchHubMembers`).
 */
export async function fetchProjectCatalogRaw(projectId: string): Promise<RawProjectCatalog> {
  const [filterResponse, frameResponse, stickerResponse] = await Promise.all([
    fetch(`${apiUrl()}/api/filters?projectId=${projectId}`, {
      credentials: 'include',
      cache: 'no-cache'
    }),
    fetch(`${apiUrl()}/api/frames?projectId=${projectId}`, {
      credentials: 'include',
      cache: 'no-cache'
    }),
    fetch(`${apiUrl()}/api/stickers?projectId=${projectId}`, {
      credentials: 'include',
      cache: 'no-cache'
    })
  ])

  if (!filterResponse.ok) {
    throw new Error(`${filterResponse.statusText || 'Failed to fetch filters'}`)
  }
  if (!frameResponse.ok) {
    throw new Error(`${frameResponse.statusText || 'Failed to fetch frames'}`)
  }
  if (!stickerResponse.ok) {
    throw new Error(`${stickerResponse.statusText || 'Failed to fetch stickers'}`)
  }

  return {
    filters: (await filterResponse.json()) as Filter[],
    frameData: (await frameResponse.json()) as RawProjectCatalog['frameData'],
    stickers: (await stickerResponse.json()) as Sticker[]
  }
}

/** `GET /api/projects/version` → `effectiveUpdatedAt`, or throws (network) / null (not ok). */
export async function fetchProjectVersion(projectId: string): Promise<string | null> {
  const res = await fetch(`${apiUrl()}/api/projects/version?id=${projectId}`, {
    credentials: 'include',
    cache: 'no-cache'
  })
  if (!res.ok) return null
  const payload = (await res.json()) as { effectiveUpdatedAt?: string }
  return typeof payload.effectiveUpdatedAt === 'string' ? payload.effectiveUpdatedAt : null
}
